import type { BuyRequest, Product } from '@/js/services/RogueTypes'
import type { DimensionOverrides } from '@rogue/rackbuilder'
import type { ProductMeasurementConfig } from '../../../RackTypes'

// Both published faces are outside-upright to outside-upright, so each one adds an upright at either end. Rogue's own RM-4 spec is 49" × 49" on a 43"-wide, 43"-deep rack in 3x3" steel, and it carries j-cups and safeties without counting them — the number is the frame, never the floor footprint.
const HYBRID_UPRIGHT_WIDTH = 3

function getHybridRackAndUnits(buyRequest: BuyRequest): BuyRequest[] {
  const rackBR = buyRequest.children?.[0]
  // Same filter createHybridRigConfig uses — children[1], the never-rendered addon template, has no side tag and is excluded.
  const units = buyRequest.children?.filter(child => child.metadata?.side) ?? []

  return rackBR ? [rackBR, ...units] : units
}

// mass_width and rack_depth are both inside-clear inches on the resolved simple ('43"'), the same face the Mass Storage drawing names its units by. Walked down children[0] like getHybridBundleSimple, but through the products map the measurement config is handed rather than a live rogue.
function getHybridSectionInches(sectionBR: BuyRequest, products: Record<string, Product>, attributeCode: 'mass_width' | 'rack_depth'): number | undefined {
  let br = sectionBR
  while (br.children?.length) br = br.children[0]

  const raw = products[String(br.productId)]?.attributes?.[attributeCode]?.toString().replace('"', '').trim()
  if (!raw) return undefined

  const inches = Number(raw)

  return Number.isFinite(inches) && inches > 0 ? inches : undefined
}

// Sections chain along one axis and share the upright at each junction, so N sections carry N+1 uprights.
function getHybridFrameWidth(buyRequest: BuyRequest, products: Record<string, Product>): number | undefined {
  const sections = getHybridRackAndUnits(buyRequest)
  if (!sections.length) return undefined

  let total = HYBRID_UPRIGHT_WIDTH

  for (const section of sections) {
    const width = getHybridSectionInches(section, products, 'mass_width')
    // One unresolved section would silently shorten the whole rig rather than fail, so hand the width back to the bounding box instead.
    if (width === undefined) return undefined

    total += width + HYBRID_UPRIGHT_WIDTH
  }

  return total
}

// Read off the rack alone: rack_depth is one of HYBRID_RACK_LEVEL_ATTRIBUTE_CODES, pinned onto every unit from the rack's own simple, so depth never varies across a rig and never grows as units are added.
function getHybridFrameDepth(buyRequest: BuyRequest, products: Record<string, Product>): number | undefined {
  const rackBR = buyRequest.children?.[0]
  if (!rackBR) return undefined

  const depth = getHybridSectionInches(rackBR, products, 'rack_depth')

  return depth === undefined ? undefined : depth + HYBRID_UPRIGHT_WIDTH * 2
}

// Section count (rack plus units) alone drives zoom — the rig only ever grows along the width axis.
export function getHybridZoomLevel(buyRequest: BuyRequest): number {
  const sectionCount = getHybridRackAndUnits(buyRequest).length

  if (sectionCount >= 5) return 1.5
  if (sectionCount >= 3) return 2

  return 2.5
}

// An axis that cannot be resolved is left off so the engine falls that direction back to the bounding box; the other axis is still worth overriding on its own.
export function getHybridDimensionOverrides(buyRequest: BuyRequest, products: Record<string, Product>): DimensionOverrides | null {
  const width = getHybridFrameWidth(buyRequest, products)
  const depth = getHybridFrameDepth(buyRequest, products)

  if (width === undefined && depth === undefined) return null

  return { north: width, south: width, east: depth, west: depth }
}

export const HYBRID_MEASUREMENT_CONFIG: ProductMeasurementConfig = {
  dynamic               : false,
  dynamicOffset         : 0.1,
  dynamicThickness      : 0.001,
  verticalPosition      : 0.02,
  dynamicCapLength      : 0.03,
  defaultZoomLevel      : 2,
  getZoomLevel          : getHybridZoomLevel,
  getDimensionOverrides : getHybridDimensionOverrides,
}
